﻿import { useState, useMemo, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { toast } from 'sonner';
import {
  ArrowLeft,
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  Clock,
  AlertTriangle,
  X,
  FileText,
  Gift,
  Plus,
  Pencil,
  LogIn,
  LogOut,
} from 'lucide-react';
import { attendanceService } from '@/services/attendance.service';
import { useInvalidateAttendance } from '@/hooks/attendance/useInvalidateAttendance';
import { MyPenaltiesTab } from './MyPenaltiesTab';
import type { DailyAttendanceRecord, AttendanceCorrection } from '@pms/shared';
import type { ApiError } from '@pms/shared';
import { Button } from '@/components/ui/button';
import { TimePicker } from '@/components/ui/time-picker';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';

// ── Helpers ────────────────────────────────────────────────────────────────────

function getErrorMessage(e: unknown): string {
  const ax = e as AxiosError<ApiError>;
  return ax.response?.data?.error?.message ?? 'Something went wrong';
}

function pad(n: number) { return String(n).padStart(2, '0'); }

function getFirstDay(year: number, month: number): string {
  return `${year}-${pad(month + 1)}-01`;
}

function getLastDay(year: number, month: number): string {
  const d = new Date(year, month + 1, 0);
  return `${year}-${pad(month + 1)}-${pad(d.getDate())}`;
}

function getDaysInMonth(year: number, month: number): number {
  return new Date(year, month + 1, 0).getDate();
}

function getMonthLabel(year: number, month: number): string {
  return new Date(year, month, 1).toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });
}

function formatHours(mins: number): string {
  if (!mins || mins <= 0) return '0h 0m';
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

/**
 * Compact cell time — "9:03am". Dropping the leading zero and the space before
 * am/pm is what buys the calendar cell enough width to render these at a
 * readable size on a 375px phone instead of the old 8px text.
 */
function formatCellPunchTime(iso: string | null | undefined): string {
  if (!iso) return '';
  return new Date(iso)
    .toLocaleTimeString('en-IN', {
      hour: 'numeric', minute: '2-digit', hour12: true, timeZone: 'Asia/Kolkata',
    })
    .replace(/\s/g, '');
}

// ── Company-paid days ─────────────────────────────────────────────────────────
// An approved company-paid day (employee_paid_hours) with no punches arrives from
// the API as a 'holiday' row carrying paid_hours_approved. It gets its own teal
// "Company Paid" treatment — it is neither a worked day nor an absence.

const paidHoursOf = (r: DailyAttendanceRecord | null | undefined): number =>
  Number(r?.paid_hours_approved ?? 0);

/** True for a day whose entire attendance is company-paid (no punches of its own). */
const isCompanyPaid = (r: DailyAttendanceRecord | null | undefined): boolean =>
  paidHoursOf(r) > 0;

/** Pseudo-status fed to the class/label helpers below. */
const statusOf = (r: DailyAttendanceRecord): string =>
  isCompanyPaid(r) ? 'company_paid' : r.status;

// Returns tailwind bg+text classes for a status
function statusClasses(status: string): string {
  switch (status) {
    case 'company_paid':   return 'bg-teal-50 text-teal-700 dark:bg-teal-900/30 dark:text-teal-300';
    case 'present':        return 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300';
    case 'late':           return 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300';
    case 'work_from_home': return 'bg-teal-100 text-teal-700 dark:bg-teal-900/40 dark:text-teal-300';
    case 'half_day':       return 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300';
    case 'absent':         return 'bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300';
    case 'holiday':        return 'bg-violet-50 text-violet-600 dark:bg-violet-900/30 dark:text-violet-300';
    case 'leave':          return 'bg-purple-100 text-purple-600 dark:bg-purple-900/40 dark:text-purple-300';
    default:               return 'bg-slate-50 text-slate-400 dark:bg-muted/50 dark:text-slate-500';
  }
}

function statusLabel(status: string): string {
  const map: Record<string, string> = {
    present: 'Present', late: 'Late', work_from_home: 'WFH',
    half_day: 'Half', absent: 'Absent', holiday: 'Holiday', leave: 'Leave',
    company_paid: 'Company Paid',
  };
  return map[status] ?? status;
}

function statusLabelShort(status: string): string {
  const map: Record<string, string> = {
    present: 'Pres', late: 'Pres', work_from_home: 'WFH',
    half_day: 'Half', absent: 'Abs', holiday: 'Hol', leave: 'Lv',
    company_paid: 'CP',
  };
  return map[status] ?? status;
}

// ── Correction reason parser ───────────────────────────────────────────────────

interface ParsedReason {
  correctionType: 'add_missing' | 'edit_time' | null;
  detail: string | null;
  text: string;
}

function parseCorrectionReason(reason: string | null | undefined): ParsedReason {
  if (!reason) return { correctionType: null, detail: null, text: '' };
  const match = reason.match(/^\[(.+?)\s*\|\s*(.+?)\]\s*([\s\S]*)/);
  if (match) {
    const typeRaw = match?.[1]?.trim() ?? '';
    const detail  = match?.[2]?.trim() ?? '';
    const text    = match?.[3]?.trim() ?? '';
    if (typeRaw === 'Add Missing Punch') return { correctionType: 'add_missing', detail, text };
    if (typeRaw === 'Edit Punch Time')   return { correctionType: 'edit_time',   detail, text };
  }
  return { correctionType: null, detail: null, text: reason };
}

// ── Missed punch type options ──────────────────────────────────────────────────

const MISSED_TYPES = [
  'In - At Morning',
  'Out - Start Lunch',
  'In - Complete Lunch',
  'Out - Short Break',
  'In - Complete Short Break',
  'Out - Complete Day',
] as const;
type MissedType = typeof MISSED_TYPES[number];

// ── Stats ──────────────────────────────────────────────────────────────────────

function computeStats(records: DailyAttendanceRecord[]) {
  // Company-paid hours stay out of `present` and `workMins` — payroll credits those
  // hours separately, so counting them as worked time would double-count them here.
  const paidHours    = records.reduce((s, r) => s + paidHoursOf(r), 0);
  const paidDays     = records.filter(isCompanyPaid).length;
  const present      = records.filter((r) => ['present', 'late', 'work_from_home'].includes(r.status)).length;
  const workMins     = records.reduce((s, r) => s + (r.total_working_minutes ?? 0), 0);
  const leave        = records.filter((r) => r.status === 'leave').length;
  const overtimeMins = records.reduce((s, r) => s + (r.overtime_minutes ?? 0), 0);
  const graceMins    = records.reduce((s, r) => s + (r.grace_minutes ?? 0), 0);
  const penaltyMins  = records.reduce((s, r) => s + (r.penalty_approved_minutes ?? 0), 0);
  return { present, workMins, leave, overtimeMins, graceMins, penaltyMins, paidHours, paidDays };
}

// ── Calendar grid ──────────────────────────────────────────────────────────────

const DAY_HEADERS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

interface CalendarGridProps {
  year:      number;
  month:     number;
  recordMap: Map<string, DailyAttendanceRecord>;
  isLoading: boolean;
  onDayClick: (record: DailyAttendanceRecord | null, dateStr: string) => void;
}

function CalendarGrid({ year, month, recordMap, isLoading, onDayClick }: CalendarGridProps) {
  const today = new Date();
  const todayStr = `${today.getFullYear()}-${pad(today.getMonth() + 1)}-${pad(today.getDate())}`;

  const daysInMonth   = getDaysInMonth(year, month);
  const firstWeekDay  = new Date(year, month, 1).getDay(); // 0=Sun

  const totalCells = Math.ceil((firstWeekDay + daysInMonth) / 7) * 7;

  return (
    <div className="rounded-2xl border border-slate-200/80 dark:border-border bg-white/80 dark:bg-card/80 shadow-sm backdrop-blur-sm overflow-hidden">
      {/* Legend */}
      <div className="flex flex-wrap items-center justify-center sm:justify-end gap-x-3 gap-y-1 px-2 sm:px-4 py-2 sm:py-2.5 border-b border-slate-100 dark:border-border/60">
        {[
          { label: 'Present', cls: 'bg-emerald-400 dark:bg-emerald-600' },
          { label: 'Half',    cls: 'bg-amber-400 dark:bg-amber-600' },
          { label: 'Absent',  cls: 'bg-rose-300 dark:bg-rose-500' },
          { label: 'Co. Paid', cls: 'bg-teal-300 dark:bg-teal-500' },
          { label: 'Miss Punch', cls: 'bg-red-500' },
        ].map((l) => (
          <span key={l.label} className="flex items-center gap-1 text-[11px] text-slate-500 dark:text-slate-400">
            <span className={`h-2.5 w-2.5 rounded-full ${l.cls}`} />
            {l.label}
          </span>
        ))}
      </div>

      {/* Day headers */}
      <div className="grid grid-cols-7 border-b border-slate-100 dark:border-border/60">
        {DAY_HEADERS.map((d, i) => (
          <div key={i} className="py-1.5 sm:py-2 text-center text-[11px] sm:text-xs font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500">
            {d}
          </div>
        ))}
      </div>

      {/* Cells */}
      {isLoading ? (
        <div className="grid grid-cols-7 gap-1 sm:gap-1.5 p-1 sm:p-2">
          {Array.from({ length: 35 }).map((_, i) => (
            <div key={i} className="h-[84px] sm:h-[96px] rounded-lg sm:rounded-xl bg-slate-100 dark:bg-muted animate-pulse" />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-7 gap-1 sm:gap-1.5 p-1 sm:p-2">
          {Array.from({ length: totalCells }).map((_, idx) => {
            const dayNum = idx - firstWeekDay + 1;
            if (dayNum < 1 || dayNum > daysInMonth) {
              return <div key={idx} className="h-[84px] sm:h-[96px]" />;
            }

            const dateStr  = `${year}-${pad(month + 1)}-${pad(dayNum)}`;
            const record   = recordMap.get(dateStr);
            const isToday  = dateStr === todayStr;
            const isFuture = dateStr > todayStr;
            const isPastAbsent = !record && !isFuture && dateStr < todayStr;
            const paidHrs      = paidHoursOf(record);
            // Same fact the miss punch penalty uses (check-ins ≠ check-outs). Punch types always
            // alternate from check_in, so this means the last punch-out is missing. Today included.
            const isMissPunch  = !!record?.has_unbalanced_punches;

            return (
              <div
                key={idx}
                onClick={() => !isFuture && onDayClick(record ?? null, dateStr)}
                className={`h-[84px] sm:h-[96px] flex flex-col p-1 sm:p-2 rounded-lg sm:rounded-xl transition-all overflow-hidden
                  ${record ? statusClasses(statusOf(record)) : isFuture ? 'text-slate-300 dark:text-slate-700' : isPastAbsent ? statusClasses('absent') : 'bg-slate-50 dark:bg-muted/40 text-slate-400 dark:text-slate-500'}
                  ${isToday ? 'ring-2 ring-teal-500 ring-offset-1 dark:ring-offset-background' : isMissPunch ? 'ring-1 ring-red-400' : ''}
                  ${isFuture ? 'cursor-default opacity-40' : 'cursor-pointer hover:ring-2 hover:ring-primary/40 hover:ring-offset-1 dark:hover:ring-offset-slate-900'}
                `}
              >
                {/* Top row: date number + status label */}
                <div className="flex items-start justify-between gap-0.5">
                  <span className={`text-sm sm:text-base leading-none ${isToday ? 'font-bold' : 'font-semibold'}`}>{dayNum}</span>
                  {isMissPunch ? (
                    <span
                      className="inline-flex items-center gap-0.5 rounded-full bg-red-500 px-1 sm:px-1.5 py-0.5 text-[8px] sm:text-[10px] font-bold leading-none text-white shrink-0"
                      title="Punch out missing"
                    >
                      <AlertTriangle className="h-2.5 w-2.5" />
                      <span className="hidden sm:inline">Miss Punch</span>
                    </span>
                  ) : record ? (
                    <span className="text-[9px] sm:text-[11px] leading-none font-semibold opacity-80 shrink-0 truncate">
                      {statusLabelShort(statusOf(record))}
                    </span>
                  ) : (!isFuture && dateStr < todayStr) && (
                    <span className="text-[9px] sm:text-[11px] leading-none font-semibold opacity-60 shrink-0">
                      Abs
                    </span>
                  )}
                </div>
                {/* Bottom: start → end time + hours */}
                <div className="mt-auto flex flex-col gap-0.5 min-w-0">
                  {paidHrs > 0 && (
                    <span className="flex items-center gap-0.5 text-[10px] sm:text-xs leading-none font-semibold opacity-90">
                      <Gift className="h-2.5 w-2.5 shrink-0" />
                      {paidHrs}h paid
                    </span>
                  )}
                  {record?.first_punch_in && (
                    // Two stacked lines while the cell is narrow; one "in → out" line from `lg`
                    // up, where a cell is finally wide enough to hold both times side by side.
                    <span className="flex flex-wrap items-center gap-x-1 text-[10px] sm:text-xs leading-tight opacity-90 tabular-nums font-medium">
                      <span className="whitespace-nowrap">{formatCellPunchTime(record.first_punch_in)}</span>
                      <span className="hidden lg:inline opacity-60">→</span>
                      <span className="w-full lg:w-auto whitespace-nowrap opacity-75 lg:opacity-100">
                        {isMissPunch
                          ? <span className="text-red-600 dark:text-red-400 font-semibold">Missing</span>
                          : record.last_punch_out ? formatCellPunchTime(record.last_punch_out) : '—'}
                      </span>
                    </span>
                  )}
                  {record && record.total_working_minutes > 0 && (
                    <span className="text-[10px] sm:text-xs leading-none opacity-100 font-bold">
                      {formatHours(record.effective_working_minutes)}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Punch Correction Type Modal (first popup) ────────────────────────────────────

function PunchCorrectionTypeModal({
  onSelect,
  onClose,
}: {
  onSelect: (type: 'add_missing' | 'edit_time') => void;
  onClose: () => void;
}) {
  return (
    <div
      className="fixed inset-0 z-modal flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="w-full max-w-md rounded-2xl border border-slate-200 dark:border-border bg-white dark:bg-card shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-slate-100 dark:border-border/60 px-5 py-4">
          <div>
            <h2 className="text-base font-bold text-slate-800 dark:text-slate-100">Punch Correction</h2>
            <p className="text-xs text-slate-400 dark:text-slate-500 mt-0.5">Choose the type of correction you need</p>
          </div>
          <button
            onClick={onClose}
            className="rounded-lg p-1.5 text-slate-400 dark:text-slate-500 hover:bg-slate-100 dark:hover:bg-muted transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Options */}
        <div className="space-y-3 p-5">
          <button
            onClick={() => onSelect('add_missing')}
            className="flex w-full items-start gap-4 rounded-xl border-2 border-slate-900 dark:border-slate-500 bg-white dark:bg-muted p-4 text-left transition hover:bg-slate-50 dark:hover:bg-muted"
          >
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-orange-100 dark:bg-orange-900/40 text-orange-500">
              <Plus className="h-5 w-5" />
            </div>
            <div>
              <p className="text-sm font-bold text-slate-800 dark:text-slate-100">Add Missing Punch</p>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">Add a punch that was completely missed (not recorded)</p>
            </div>
          </button>

          <button
            onClick={() => onSelect('edit_time')}
            className="flex w-full items-start gap-4 rounded-xl border border-slate-200 dark:border-border bg-white dark:bg-muted p-4 text-left transition hover:border-slate-400 dark:hover:border-border hover:bg-slate-50 dark:hover:bg-muted"
          >
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-violet-100 dark:bg-violet-900/40 text-violet-500">
              <Pencil className="h-5 w-5" />
            </div>
            <div>
              <p className="text-sm font-bold text-slate-800 dark:text-slate-100">Edit Punch Time</p>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">Update the time of an existing punch (recorded at wrong time)</p>
            </div>
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Punch Correction Form Modal (second popup) ───────────────────────────────────

function PunchCorrectionFormModal({
  correctionType,
  fullDateLabel,
  punchOptions,
  missedType, setMissedType,
  missedTime, setMissedTime,
  whichPunch, setWhichPunch,
  correctTime, setCorrectTime,
  reason, setReason,
  canSubmit,
  isPending,
  onBack,
  onClose,
  onSubmit,
}: {
  correctionType: 'add_missing' | 'edit_time';
  fullDateLabel: string;
  punchOptions: { value: string; punchType: 'check_in' | 'check_out'; label: string; time: string; disabled: boolean }[];
  missedType: MissedType | '';
  setMissedType: (v: MissedType | '') => void;
  missedTime: string;
  setMissedTime: (v: string) => void;
  whichPunch: string;
  setWhichPunch: (v: string) => void;
  correctTime: string;
  setCorrectTime: (v: string) => void;
  reason: string;
  setReason: (v: string) => void;
  canSubmit: boolean;
  isPending: boolean;
  onBack: () => void;
  onClose: () => void;
  onSubmit: () => void;
}) {
  const title = correctionType === 'add_missing' ? 'Add Missing Punch' : 'Edit Punch Time';

  return (
    <div
      className="fixed inset-0 z-modal-nested flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="relative w-full max-w-md rounded-2xl border border-slate-200 dark:border-border bg-white dark:bg-card shadow-2xl max-h-[90vh] flex flex-col">

        {/* Header */}
        <div className="flex items-center gap-2 border-b border-slate-100 dark:border-border/60 px-4 py-3.5 shrink-0">
          <button
            onClick={onBack}
            className="rounded-lg p-1.5 text-slate-400 dark:text-slate-500 hover:bg-slate-100 dark:hover:bg-muted transition-colors"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <p className="flex-1 text-sm font-bold text-slate-800 dark:text-slate-100">{title}</p>
          <button
            onClick={onClose}
            className="rounded-lg p-1.5 text-slate-400 dark:text-slate-500 hover:bg-slate-100 dark:hover:bg-muted transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Scrollable body */}
        <div className="overflow-y-auto flex-1">

          {/* Add Missing Punch form */}
          {correctionType === 'add_missing' && (
            <div className="space-y-4 p-5">
              {/* Date (read-only) */}
              <div>
                <label className="mb-1.5 block text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase tracking-wide">Date</label>
                <div className="flex items-center gap-2 rounded-xl border border-slate-200 dark:border-border bg-slate-50 dark:bg-muted px-3 py-2.5 text-sm text-slate-600 dark:text-slate-300">
                  <CalendarDays className="h-3.5 w-3.5 text-slate-400 dark:text-slate-500 shrink-0" />
                  {fullDateLabel}
                </div>
              </div>
              {/* Missed Type */}
              <div>
                <label className="mb-1.5 block text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase tracking-wide">Missed Type <span className="text-rose-500">*</span></label>
                <Select value={missedType || undefined} onValueChange={(v) => setMissedType(v as MissedType)}>
                  <SelectTrigger className="w-full rounded-xl border-slate-200 dark:border-border bg-white dark:bg-muted text-sm text-slate-700 dark:text-slate-200 focus:ring-teal-500/30">
                    <SelectValue placeholder="Select missed type" />
                  </SelectTrigger>
                  <SelectContent>
                    {MISSED_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {/* Missed Time */}
              <div>
                <label className="mb-1.5 block text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase tracking-wide">Missed Time <span className="text-rose-500">*</span></label>
                <TimePicker value={missedTime} onChange={setMissedTime} />
              </div>
              {/* Reason */}
              <div>
                <label className="mb-1.5 block text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase tracking-wide">
                  Reason <span className="text-rose-500">*</span>
                  <span className="ml-1 normal-case font-normal text-slate-400 dark:text-slate-500">(min 10 chars)</span>
                </label>
                <textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  rows={4}
                  maxLength={440}
                  placeholder="Explain why your punch was missed…"
                  className="w-full resize-none rounded-xl border border-slate-200 dark:border-border bg-white dark:bg-muted px-3 py-2.5 text-sm text-slate-700 dark:text-slate-200 placeholder:text-slate-400 dark:placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-teal-500/30"
                />
                <p className="mt-1 text-right text-[11px] text-slate-400 dark:text-slate-500">{reason.length}/440</p>
              </div>
            </div>
          )}

          {/* Edit Punch Time form */}
          {correctionType === 'edit_time' && (
            <div className="space-y-4 p-5">
              {/* Info banner */}
              <div className="flex items-start gap-2 rounded-xl border border-violet-200 dark:border-violet-800 bg-violet-50 dark:bg-violet-900/20 px-3.5 py-3">
                <FileText className="mt-0.5 h-4 w-4 shrink-0 text-violet-400 dark:text-violet-500" />
                <p className="text-xs text-violet-700 dark:text-violet-300">
                  Select the punch to edit. It will be <strong>updated after manager approval.</strong>
                </p>
              </div>
              {/* Punch selection */}
              <div>
                <label className="mb-1.5 block text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase tracking-wide">Select Punch to Edit <span className="text-rose-500">*</span></label>
                <div className="space-y-2">
                  {punchOptions.map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      disabled={opt.disabled}
                      onClick={() => !opt.disabled && setWhichPunch(opt.value)}
                      className={`flex w-full items-center gap-3 rounded-xl border px-3.5 py-3 text-left transition
                        ${whichPunch === opt.value
                          ? 'border-teal-500 bg-teal-50 dark:bg-teal-900/20'
                          : 'border-slate-200 dark:border-border bg-white dark:bg-muted hover:border-slate-300 dark:hover:border-border'}
                        ${opt.disabled ? 'cursor-not-allowed opacity-40' : 'cursor-pointer'}
                      `}
                    >
                      <div className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-full border-2
                        ${whichPunch === opt.value ? 'border-teal-500' : 'border-slate-300 dark:border-border'}`}>
                        {whichPunch === opt.value && <div className="h-2 w-2 rounded-full bg-teal-500" />}
                      </div>
                      {opt.punchType === 'check_in'
                        ? <LogIn  className="h-4 w-4 text-teal-500 shrink-0" />
                        : <LogOut className="h-4 w-4 text-rose-400 shrink-0" />
                      }
                      <span className="flex-1 text-sm font-semibold text-slate-700 dark:text-slate-200">{opt.time}</span>
                      <span className={`rounded-full px-2.5 py-0.5 text-[11px] font-semibold
                        ${opt.punchType === 'check_in'
                          ? 'bg-teal-100 dark:bg-teal-900/40 text-teal-700 dark:text-teal-300'
                          : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300'}`}>
                        {opt.label}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
              {/* Correct time */}
              <div>
                <label className="mb-1.5 block text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase tracking-wide">Correct Time <span className="text-rose-500">*</span></label>
                <TimePicker value={correctTime} onChange={setCorrectTime} />
              </div>
              {/* Reason */}
              <div>
                <label className="mb-1.5 block text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase tracking-wide">
                  Reason for Edit <span className="text-rose-500">*</span>
                  <span className="ml-1 normal-case font-normal text-slate-400 dark:text-slate-500">(min 10 chars)</span>
                </label>
                <textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  rows={4}
                  maxLength={440}
                  placeholder="Explain why these punch times need to be corrected…"
                  className="w-full resize-none rounded-xl border border-slate-200 dark:border-border bg-white dark:bg-muted px-3 py-2.5 text-sm text-slate-700 dark:text-slate-200 placeholder:text-slate-400 dark:placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-teal-500/30"
                />
                <p className="mt-1 text-right text-[11px] text-slate-400 dark:text-slate-500">{reason.length}/440</p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 border-t border-slate-100 dark:border-border/60 px-5 py-3.5 shrink-0">
          <Button variant="outline" size="sm" onClick={onClose} disabled={isPending}>Cancel</Button>
          <Button size="sm" onClick={onSubmit} disabled={!canSubmit}>
            {isPending ? 'Submitting…' : 'Submit Request'}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ── Day Detail Modal (with inline correction panel + punch timeline) ────────────

function DayDetailModal({
  record,
  dateStr,
  onClose,
  onCorrectionSuccess,
}: {
  record: DailyAttendanceRecord | null;
  dateStr: string;
  onClose: () => void;
  onCorrectionSuccess: () => void;
}) {
  // Inline correction panel state
  const [correctionOpen, setCorrectionOpen]   = useState(false);
  const [correctionType, setCorrectionType]   = useState<'add_missing' | 'edit_time' | null>(null);
  const [formModalOpen, setFormModalOpen]     = useState(false);
  // Prevent duplicate submissions: stays true from onSuccess until query refetch clears
  const [submittedPending, setSubmittedPending] = useState(false);

  // Fetch today's corrections for this record from the server (max 5 per day)
  const { data: todayCorrectionsData, refetch: refetchTodayCorrections } = useQuery({
    queryKey: ['my-corrections-record', record?.id],
    queryFn: () => attendanceService.listMyCorrections({ daily_record_id: record!.id, limit: 100 }),
    enabled: !!record,
  });
  const correctionCount = todayCorrectionsData?.data?.length ?? 0;
  // Add Missing Punch form
  const [missedType,  setMissedType]  = useState<MissedType | ''>('');
  const [missedTime,  setMissedTime]  = useState('');
  // Edit Punch Time form
  const [whichPunch,  setWhichPunch]  = useState('');
  const [correctTime, setCorrectTime] = useState('');
  // Shared
  const [reason, setReason] = useState('');

  const invalidateAttendance = useInvalidateAttendance();

  // Fetch individual punch logs for this day
  const { data: punchLogs, isLoading: punchesLoading } = useQuery({
    queryKey: ['my-punches', dateStr],
    queryFn: () => attendanceService.getMyPunchLogs({ date_from: dateStr, date_to: dateStr }),
    enabled: !!record,
  });

  const mutation = useMutation({
    mutationFn: () => {
      if (!record) throw new Error('No record');
      if (correctionType === 'add_missing') {
        const [mh, mm] = missedTime.split(':').map(Number);
        const missedTimeLabel = new Date(2000, 0, 1, mh, mm).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });
        const fullReason = `[Add Missing Punch | ${missedType} — ${missedTimeLabel}] ${reason.trim()}`;
        const requestedPunchType = missedType.startsWith('In') ? 'check_in' : 'check_out';
        const requestedPunchTime = `${dateStr}T${missedTime}:00+05:30`; // IST offset
        return attendanceService.createCorrection(record.id, fullReason, {
          requested_punch_type: requestedPunchType,
          requested_punch_time: requestedPunchTime,
        });
      } else {
        const selected   = punchOptions.find((o) => o.value === whichPunch);
        const punchLabel = selected?.punchType === 'check_in' ? 'Check In' : 'Check Out';
        const oldTime     = selected?.time ?? '?';
        const [ch, cm]    = correctTime.split(':').map(Number);
        const newTime     = correctTime
          ? new Date(2000, 0, 1, ch, cm).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true })
          : '?';
        const fullReason = `[Edit Punch Time | ${punchLabel}: ${oldTime} → ${newTime}] ${reason.trim()}`;
        const requestedPunchTime = `${dateStr}T${correctTime}:00+05:30`; // IST offset
        return attendanceService.createCorrection(record.id, fullReason, {
          punch_log_id: whichPunch,
          requested_punch_time: requestedPunchTime,
        });
      }
    },
    onSuccess: () => {
      toast.success(correctionType === 'add_missing' ? 'Missing punch request submitted' : 'Edit request submitted');
      setSubmittedPending(true);
      void refetchTodayCorrections();
      invalidateAttendance();
      setFormModalOpen(false);
      closeCorrection();
      onCorrectionSuccess();
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  // Once the parent record prop reflects the new status, clear the local guard
  useEffect(() => {
    if (record?.regularization_status === 'requested') {
      setSubmittedPending(false);
    }
  }, [record?.regularization_status]);

  const todayStr = (() => { const t = new Date(); return `${t.getFullYear()}-${pad(t.getMonth()+1)}-${pad(t.getDate())}`; })();
  const isToday  = dateStr === todayStr;

  const date         = new Date(dateStr + 'T00:00:00');
  const dayNum       = date.getDate();
  const weekday      = date.toLocaleDateString('en-IN', { weekday: 'long' });
  const dateLabel    = date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  const fullDateLabel = date.toLocaleDateString('en-GB', { weekday: 'long', day: '2-digit', month: 'short', year: 'numeric' });

  // Today only, whatever the status (leave included) — as long as there is at least one punch.
  const hasMissedPunch =
    !!record &&
    isToday &&
    (punchLogs?.length ?? 0) > 0;

  const canSubmit =
    correctionType === 'add_missing'
      ? missedType !== '' && missedTime !== '' && reason.trim().length >= 10 && !mutation.isPending
      : correctionType === 'edit_time'
      ? whichPunch !== '' && correctTime !== '' && reason.trim().length >= 10 && !mutation.isPending
      : false;

  function toIST(iso: string | null | undefined): string {
    if (!iso) return '–';
    return new Date(iso).toLocaleTimeString('en-IN', {
      hour: '2-digit', minute: '2-digit', hour12: true, timeZone: 'Asia/Kolkata',
    });
  }

  const punchOptions = (punchLogs ?? []).map((p) => ({
    value:     p.id,
    punchType: p.punch_type,
    label:     p.punch_type === 'check_in' ? 'Check In' : 'Check Out',
    time:      toIST(p.punch_time),
    disabled:  false,
  }));

  function openCorrection() {
    setCorrectionOpen(true);
    setCorrectionType(null);
    setFormModalOpen(false);
  }

  function closeCorrection() {
    setCorrectionOpen(false);
    setCorrectionType(null);
    setFormModalOpen(false);
    setMissedType('');
    setMissedTime('');
    setWhichPunch('');
    setCorrectTime('');
    setReason('');
  }

  return createPortal(
    <div
      className="fixed inset-0 z-modal-nested flex items-end justify-center bg-black/60 backdrop-blur-md"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="relative w-full rounded-t-2xl bg-card shadow-2xl max-h-[90vh] overflow-y-auto animate-in slide-in-from-bottom duration-300">

        <div className="sticky top-0 z-20 bg-teal-800 rounded-t-2xl text-white">
          {/* Handle bar inside teal header — no white gap */}
          <div className="flex justify-center pt-3 pb-2">
            <div className="h-1 w-10 rounded-full bg-white/20" />
          </div>
          {/* Title row */}
          <div className="flex items-center gap-3 px-5 pb-3.5">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white/20 text-lg font-bold">
              {dayNum}
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="text-sm font-semibold">{weekday}</h2>
              <p className="text-xs text-teal-200">{dateLabel}</p>
            </div>
            <button onClick={onClose} className="flex h-7 w-7 items-center justify-center rounded-full bg-white/10 hover:bg-white/20 transition-colors">
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* No record */}
        {!record ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <FileText className="mb-2 h-8 w-8 text-muted-foreground/40" />
            <p className="text-sm text-muted-foreground">No attendance data for this date</p>
          </div>
        ) : (
          <div className="space-y-0">

            {/* Status bar */}
            <div className="flex items-center gap-3 px-5 py-3 border-b border-border/60">
              <span className={`inline-flex items-center rounded-full px-3 py-0.5 text-xs font-semibold ${statusClasses(statusOf(record))}`}>
                {statusLabel(statusOf(record))}
              </span>
              {record.effective_working_minutes > 0 && (
                <span className="text-sm font-semibold text-foreground">
                  {formatHours(record.effective_working_minutes)} worked
                </span>
              )}
            </div>

            {/* Company-paid hours — the reason this day is not an absence */}
            {paidHoursOf(record) > 0 && (
              <div className="border-b border-border/60 px-5 py-4">
                <div className="flex items-start gap-2.5 rounded-xl border border-teal-200 dark:border-teal-800/50 bg-teal-50/60 dark:bg-teal-900/20 px-4 py-3">
                  <Gift className="mt-0.5 h-4 w-4 shrink-0 text-teal-600 dark:text-teal-400" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-teal-700 dark:text-teal-300">
                      {paidHoursOf(record)}h paid by the company
                    </p>
                    {record.remarks && (
                      <p className="mt-0.5 text-xs text-teal-600/80 dark:text-teal-400/80">{record.remarks}</p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Grace Breakdown — skipped on a fully company-paid day, where there is
                no punched work for a base/grace/penalty breakdown to describe. */}
            {!isCompanyPaid(record) && (
            <div className="border-b border-border/60 px-5 py-4">
              <p className="mb-2.5 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-teal-600">
                <Clock className="h-3.5 w-3.5" /> Grace Breakdown
              </p>
              <div className="rounded-xl border border-teal-200 dark:border-teal-800/50 bg-teal-50/60 dark:bg-teal-900/20 divide-y divide-teal-100 dark:divide-teal-900/40 text-sm overflow-hidden">
                {/* Base Work */}
                <div className="flex justify-between px-4 py-2.5">
                  <span className="text-muted-foreground">Base Work</span>
                  <span className="font-semibold text-foreground">{formatHours(record.total_working_minutes)}</span>
                </div>
                {/* Grace row — hidden on Sunday: grace is never added on a Sunday, so the
                    "6h required" hint would wrongly suggest the hours fell short. */}
                {new Date(dateStr + 'T00:00:00').getDay() !== 0 && (
                  <div className="flex items-center justify-between gap-3 px-4 py-2.5">
                    <span className="text-muted-foreground">Grace</span>
                    {(record.grace_minutes ?? 0) > 0 ? (
                      <span className="font-semibold text-teal-600">+{formatHours(record.grace_minutes)}</span>
                    ) : (
                      <span className="text-xs text-muted-foreground text-right leading-tight">
                        Not eligible — Worked {formatHours(record.total_working_minutes)} / 6h required
                      </span>
                    )}
                  </div>
                )}
                {/* Penalty — only approved penalties are shown/deducted */}
                {(record.penalty_approved_minutes ?? 0) > 0 && (
                  <div className="flex justify-between px-4 py-2.5">
                    <span className="text-muted-foreground">Penalty</span>
                    <span className="font-semibold text-red-500">-{formatHours(record.penalty_approved_minutes ?? 0)}</span>
                  </div>
                )}
                {/* Total */}
                <div className="flex justify-between px-4 py-2.5 bg-teal-100/40 dark:bg-teal-900/30">
                  <span className="font-semibold text-foreground">Total</span>
                  <span className="font-bold text-teal-700 dark:text-teal-400">{formatHours(record.effective_working_minutes - (record.penalty_approved_minutes ?? 0))}</span>
                </div>
              </div>
            </div>
            )}

            {/* Missed Punch card — with Request Correction button */}
            {hasMissedPunch && (
              <div className="mx-5 mb-0 mt-3 rounded-xl border border-rose-200 dark:border-rose-800 bg-rose-50 dark:bg-rose-900/20 px-4 py-3">
                <div className="flex items-start gap-2.5">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-rose-500" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-rose-700 dark:text-rose-300">Missed Punch Detected</p>
                    <p className="text-xs text-rose-500 dark:text-rose-400 mt-0.5">
                      Unmatched punch detected — a check-in or check-out is missing. Request a correction.
                    </p>
                    {isToday && !submittedPending && correctionCount < 5 && (
                      <button
                        onClick={openCorrection}
                        className="mt-2.5 inline-flex items-center gap-1.5 rounded-full border border-rose-300 bg-card px-3 py-1 text-xs font-semibold text-rose-600 hover:bg-rose-50 transition-colors"
                      >
                        <AlertTriangle className="h-3 w-3" /> Request Correction
                        <span className="opacity-60">({correctionCount}/5)</span>
                      </button>
                    )}
                    {isToday && correctionCount >= 5 && (
                      <p className="mt-2 text-[11px] font-semibold text-rose-400 dark:text-rose-500">
                        Daily limit reached — 5/5 requests used today.
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Punch Timeline */}
            <div className="border-b border-border/60 px-5 py-4">
              <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Punch Timeline
              </p>
              {punchesLoading ? (
                <div className="space-y-3">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="h-14 rounded-xl bg-muted animate-pulse" />
                  ))}
                </div>
              ) : (punchLogs && punchLogs.length > 0) ? (
                <div>
                  {punchLogs.map((p, idx) => {
                    const isIn    = p.punch_type === 'check_in';
                    const isLast  = idx === punchLogs.length - 1;
                    const score   = p.face_verification_score;
                    const showScore = typeof score === 'number' && score >= 0 && score <= 100;
                    return (
                      <div key={p.id} className="flex items-stretch gap-3">

                        {/* Left column: circle icon + connector line below */}
                        <div className="flex flex-col items-center">
                          <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full
                            ${isIn
                              ? 'bg-teal-100 dark:bg-teal-900/40 text-teal-600 dark:text-teal-400'
                              : 'bg-rose-100 dark:bg-rose-900/40 text-rose-500 dark:text-rose-400'
                            }`}>
                            {isIn ? <LogIn className="h-4 w-4" /> : <LogOut className="h-4 w-4" />}
                          </div>
                          {/* Connector line — shown between items, not after the last */}
                          {!isLast && (
                            <div className="w-px flex-1 bg-border my-1" />
                          )}
                        </div>

                        {/* Right column: label, source, match, time */}
                        <div className={`flex flex-1 items-center justify-between gap-3 min-w-0 ${!isLast ? 'pb-3' : ''}`}>
                          <div className="min-w-0">
                            <p className={`text-sm font-semibold leading-none
                              ${isIn ? 'text-teal-700' : 'text-foreground'}`}>
                              {isIn ? 'Check In' : 'Check Out'}
                            </p>
                            <div className="mt-1 flex items-center gap-1.5 flex-wrap">
                              <span className="text-[11px] text-muted-foreground capitalize">
                                {(p.punch_source ?? '—').replace(/_/g, ' ')}
                              </span>
                              {showScore && (
                                <span className={`inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-semibold
                                  ${isIn
                                    ? 'bg-teal-100 dark:bg-teal-900/40 text-teal-700 dark:text-teal-300'
                                    : 'bg-rose-100 dark:bg-rose-900/40 text-rose-600 dark:text-rose-300'
                                  }`}>
                                  {Math.round(score)}% match
                                </span>
                              )}
                            </div>
                          </div>
                          <span className="shrink-0 text-sm font-bold text-foreground">
                            {new Date(p.punch_time).toLocaleTimeString('en-IN', {
                              hour: '2-digit', minute: '2-digit', hour12: true, timeZone: 'Asia/Kolkata',
                            })}
                          </span>
                        </div>

                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-xs italic text-muted-foreground">No individual punch records found</p>
              )}
            </div>

          </div>
        )}
      </div>

      {/* ── First popup: correction type selection ── */}
      {correctionOpen && !formModalOpen && (
        <PunchCorrectionTypeModal
          onSelect={(type) => { setCorrectionType(type); setFormModalOpen(true); }}
          onClose={closeCorrection}
        />
      )}

      {/* ── Second popup: correction form modal ── */}
      {formModalOpen && correctionType && (
        <PunchCorrectionFormModal
          correctionType={correctionType}
          fullDateLabel={fullDateLabel}
          punchOptions={punchOptions}
          missedType={missedType}
          setMissedType={setMissedType}
          missedTime={missedTime}
          setMissedTime={setMissedTime}
          whichPunch={whichPunch}
          setWhichPunch={setWhichPunch}
          correctTime={correctTime}
          setCorrectTime={setCorrectTime}
          reason={reason}
          setReason={setReason}
          canSubmit={canSubmit}
          isPending={mutation.isPending}
          onBack={() => { setFormModalOpen(false); setCorrectionType(null); }}
          onClose={() => { setFormModalOpen(false); closeCorrection(); }}
          onSubmit={() => mutation.mutate()}
        />
      )}
    </div>,
    document.body
  );
}

// ── Corrections tab ────────────────────────────────────────────────────────────

type CorrectionFilterStatus = 'all' | 'approved' | 'pending' | 'rejected';

const CORRECTION_STATUS_FILTERS: { id: CorrectionFilterStatus; label: string }[] = [
  { id: 'all',      label: 'All' },
  { id: 'approved', label: 'Approved' },
  { id: 'pending',  label: 'Pending' },
  { id: 'rejected', label: 'Rejected' },
];

function CorrectionsTab({ corrections, isLoading }: { corrections: AttendanceCorrection[]; isLoading: boolean }) {
  const [filterStatus, setFilterStatus] = useState<CorrectionFilterStatus>('all');

  const filteredCorrections = filterStatus === 'all'
    ? corrections
    : corrections.filter((c) => c.status === filterStatus);

  const statusFilterChips = (
    <div className="flex flex-wrap gap-2">
      {CORRECTION_STATUS_FILTERS.map((f) => (
        <button
          key={f.id}
          onClick={() => setFilterStatus(f.id)}
          className={`rounded-full px-3.5 py-1 text-xs font-semibold transition-colors border ${
            filterStatus === f.id
              ? 'bg-teal-600 text-white border-teal-600'
              : 'bg-white dark:bg-card border-slate-200 dark:border-border text-slate-600 dark:text-slate-300 hover:border-slate-400 dark:hover:border-slate-500'
          }`}
        >
          {f.label}
        </button>
      ))}
    </div>
  );

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-40 rounded-xl bg-slate-100 dark:bg-muted animate-pulse" />
        ))}
      </div>
    );
  }

  if (filteredCorrections.length === 0) {
    return (
      <div className="space-y-4">
        {statusFilterChips}
        <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-slate-200 dark:border-border bg-white/60 dark:bg-card/60 py-14 text-center">
          <AlertTriangle className="mb-2 h-8 w-8 text-slate-300 dark:text-slate-600" />
          <p className="text-sm text-slate-500 dark:text-slate-400">
            {filterStatus !== 'all' ? `No ${filterStatus} correction requests` : 'No punch correction requests'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {statusFilterChips}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
      {filteredCorrections.map((c) => {
        const parsed = parseCorrectionReason(c.reason);

        // Left border colour by status
        const borderColor =
          c.status === 'approved' ? '#10b981'
          : c.status === 'rejected' ? '#ef4444'
          : '#f59e0b';

        const statusTextCls =
          c.status === 'approved'
            ? 'text-emerald-600 dark:text-emerald-400 text-xs font-semibold capitalize'
            : c.status === 'rejected'
              ? 'text-red-500 dark:text-red-400 text-xs font-semibold capitalize'
              : 'text-amber-600 dark:text-amber-400 text-xs font-semibold capitalize';

        const dateStr = c.attendance_date ?? c.requested_at.slice(0, 10);
        const dateObj   = new Date(dateStr + 'T00:00:00');
        const dateShort = dateObj.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
        const dayName   = dateObj.toLocaleDateString('en-IN', { weekday: 'long' });

        const requestedAt = new Date(c.requested_at).toLocaleString('en-IN', {
          day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', hour12: true,
        });

        return (
          <div
            key={c.id}
            className="overflow-hidden rounded-xl border border-slate-200/80 dark:border-border bg-white dark:bg-card shadow-sm"
            style={{ borderLeft: `4px solid ${borderColor}` }}
          >
            {/* Card header: date + status */}
            <div className="flex items-start justify-between px-4 pt-3 pb-2 border-b border-slate-100 dark:border-border/60">
              <div>
                <p className="text-base font-bold text-slate-800 dark:text-slate-100">{dateShort}</p>
                <p className="text-xs text-slate-400 dark:text-slate-500">{dayName}</p>
              </div>
              <span className={statusTextCls}>{c.status}</span>
            </div>

            {/* Card body */}
            <div className="space-y-2.5 px-4 py-3">
              {/* TYPE row */}
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500 mb-0.5">TYPE</p>
                {parsed.correctionType ? (
                  <p className="text-sm font-semibold text-slate-700 dark:text-slate-200">
                    {parsed.detail ?? (parsed.correctionType === 'add_missing' ? 'Add Missing Punch' : 'Edit Punch Time')}
                  </p>
                ) : (
                  <p className="text-sm font-semibold text-slate-400 dark:text-slate-500">—</p>
                )}
              </div>

              {/* REASON row */}
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500 mb-0.5">REASON</p>
                <p className="text-sm text-slate-600 dark:text-slate-300 leading-snug">
                  {parsed.text || c.reason || '—'}
                </p>
              </div>

              {/* REJECTION / ADMIN NOTE */}
              {c.status === 'rejected' && c.admin_remarks && (
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-red-400 mb-0.5">REJECTION REASON</p>
                  <p className="text-sm text-red-500 dark:text-red-400 leading-snug">{c.admin_remarks}</p>
                </div>
              )}
              {c.status === 'approved' && c.admin_remarks && (
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-emerald-500 mb-0.5">ADMIN NOTE</p>
                  <p className="text-sm text-emerald-600 dark:text-emerald-400 leading-snug">{c.admin_remarks}</p>
                </div>
              )}
            </div>

            {/* Card footer: submitted timestamp */}
            <div className="border-t border-slate-100 dark:border-border/60 px-4 py-2">
              <p className="text-[11px] text-slate-400 dark:text-slate-500">Submitted {requestedAt}</p>
            </div>
          </div>
        );
      })}
      </div>
    </div>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────────

export function MyAttendancePage() {
  const navigate = useNavigate();
  const [month, setMonth] = useState(() => {
    const now = new Date();
    return { year: now.getFullYear(), month: now.getMonth() };
  });
  const [activeTab, setActiveTab] = useState<'calendar' | 'corrections' | 'penalties'>('calendar');
  // Store only the dateStr; derive record reactively so it updates after mutations
  const [selectedDay, setSelectedDay] = useState<string | null>(null);

  const dateFrom = getFirstDay(month.year, month.month);
  const dateTo   = getLastDay(month.year, month.month);

  const { data, isLoading } = useQuery({
    queryKey: ['my-attendance', month.year, month.month],
    queryFn: () => attendanceService.listMyAttendance({ date_from: dateFrom, date_to: dateTo, limit: 31 }),
  });

  const records     = data?.data ?? [];
  const recordMap   = useMemo(() => new Map(records.map((r) => [r.attendance_date, r])), [records]);
  // Always derive the selected record live — so after a mutation + query invalidation the modal
  // automatically sees regularization_status: 'requested' without closing and re-opening.
  const selectedRecord = selectedDay ? (recordMap.get(selectedDay) ?? null) : null;
  const stats       = useMemo(() => computeStats(records), [records]);

  // Corrections tab: fetch all correction rows from the dedicated table
  const { data: correctionsData, isLoading: correctionsLoading } = useQuery({
    queryKey: ['my-corrections'],
    queryFn: () => attendanceService.listMyCorrections({ limit: 100 }),
    enabled: activeTab === 'corrections',
  });
  const corrections = correctionsData?.data ?? [];

  const prevMonth = () =>
    setMonth(({ year, month: m }) => m === 0 ? { year: year - 1, month: 11 } : { year, month: m - 1 });

  const nextMonth = () =>
    setMonth(({ year, month: m }) => m === 11 ? { year: year + 1, month: 0 } : { year, month: m + 1 });

  const statBoxes = [
    { label: 'PRESENT',  value: stats.present,                      border: 'border-t-emerald-500', num: 'text-emerald-600 dark:text-emerald-400' },
    { label: 'WORK HRS', value: formatHours(stats.workMins),        border: 'border-t-teal-500',    num: 'text-teal-600 dark:text-teal-400'       },
    { label: 'LEAVE',    value: stats.leave,                        border: 'border-t-amber-400',   num: 'text-amber-600 dark:text-amber-400'     },
    { label: 'GRACE',    value: stats.graceMins > 0 ? `+${formatHours(stats.graceMins)}` : '–',      border: 'border-t-teal-500',    num: stats.graceMins > 0 ? 'text-teal-600 dark:text-teal-400' : 'text-slate-400 dark:text-slate-500'       },
    { label: 'PENALTY',  value: stats.penaltyMins > 0 ? `-${formatHours(stats.penaltyMins)}` : '–', border: 'border-t-rose-500', num: stats.penaltyMins > 0 ? 'text-rose-600 dark:text-rose-400' : 'text-slate-400 dark:text-slate-500' },
  ];

  // Only surfaced in months that actually have approved company-paid hours.
  if (stats.paidDays > 0) {
    statBoxes.push({
      label:  'COMPANY PAID',
      value:  `${stats.paidHours}h`,
      border: 'border-t-teal-500',
      num:    'text-teal-600 dark:text-teal-400',
    });
  }

  return (
    <div className="space-y-4">

      {/* Page header */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate('/account')}
            className="rounded-lg p-1.5 text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-muted transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div className="flex items-center gap-2">
            <CalendarDays className="h-5 w-5 text-teal-600" />
            <h1 className="text-xl font-bold text-slate-800 dark:text-slate-100">My Attendance</h1>
          </div>
        </div>

        {/* Month navigator */}
        <div className="flex items-center gap-1 rounded-lg border border-slate-200 dark:border-border bg-white dark:bg-card px-1 py-1 ml-auto">
          <button
            onClick={prevMonth}
            className="rounded-md p-1 text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-muted transition-colors"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <span className="min-w-[110px] text-center text-sm font-semibold text-slate-700 dark:text-slate-200">
            {getMonthLabel(month.year, month.month)}
          </span>
          <button
            onClick={nextMonth}
            className="rounded-md p-1 text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-muted transition-colors"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Stats strip */}
      <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
        {statBoxes.map((s) => (
          <div
            key={s.label}
            className={`rounded-xl border border-slate-200/80 dark:border-border bg-white/80 dark:bg-card/80 pt-2.5 sm:pt-3 pb-2 sm:pb-2.5 px-1.5 sm:px-2 text-center shadow-sm backdrop-blur-sm border-t-4 ${s.border}`}
          >
            <p className={`text-base font-bold leading-none ${s.num}`}>{s.value}</p>
            <p className="mt-1 text-[10px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-0 border-b border-slate-200 dark:border-border overflow-x-auto">
        {[
          { id: 'calendar'    as const, label: 'Calendar View' },
          { id: 'corrections' as const, label: `Punch Corrections${corrections.length > 0 ? ` (${corrections.length})` : ''}` },
          { id: 'penalties'   as const, label: 'My Penalties' },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`shrink-0 whitespace-nowrap px-3 sm:px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
              activeTab === tab.id
                ? 'border-teal-600 text-teal-700 dark:text-teal-400'
                : 'border-transparent text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {activeTab === 'calendar' ? (
        <CalendarGrid
          year={month.year}
          month={month.month}
          recordMap={recordMap}
          isLoading={isLoading}
          onDayClick={(_record, dateStr) => setSelectedDay(dateStr)}
        />
      ) : activeTab === 'corrections' ? (
        <CorrectionsTab corrections={corrections} isLoading={correctionsLoading} />
      ) : (
        <MyPenaltiesTab dateFrom={dateFrom} dateTo={dateTo} />
      )}

      {/* Day detail modal — correction panel lives inside it */}
      {selectedDay && (
        <DayDetailModal
          record={selectedRecord}
          dateStr={selectedDay}
          onClose={() => setSelectedDay(null)}
          onCorrectionSuccess={() => { /* record updates reactively via recordMap after query invalidation */ }}
        />
      )}
    </div>
  );
}
